import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Label, Select } from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  qboWebhookEventsApi,
  QboWebhookEventQuery,
} from '../api/qboWebhookEventsApi';

export function QboWebhookActivityPage() {
  // Filter state
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [selectedEventType, setSelectedEventType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Build query parameters for API
  const queryParams: QboWebhookEventQuery = {
    page: currentPage,
    limit: pageSize,
    sortField: 'createdAt',
    sortDirection: 'desc',
    entityType: selectedEntityType || undefined,
    eventType: selectedEventType || undefined,
    status: selectedStatus || undefined,
  };

  // Fetch QBO webhook events
  const {
    data: webhookEventsResponse,
    isLoading: isLoadingEvents,
    error: eventsError,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: [
      'qbo-webhook-events',
      selectedEntityType,
      selectedEventType,
      selectedStatus,
      currentPage,
    ],
    queryFn: () => qboWebhookEventsApi.queryWebhookEvents(queryParams),
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
    refetchOnWindowFocus: false,
    throwOnError: false, // Don't throw on error, handle gracefully
  });

  // Fetch QBO webhook stats
  const { data: webhookStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['qbo-webhook-stats'],
    queryFn: () => qboWebhookEventsApi.getWebhookStats(),
    staleTime: 30 * 1000, // Cache for 30 seconds
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    throwOnError: false, // Don't throw on error, handle gracefully
  });

  // Extract events from response
  const events = webhookEventsResponse?.data || [];
  const totalCount = webhookEventsResponse?.total || 0;
  const hasMore = webhookEventsResponse?.hasMore || false;

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEntityType, selectedEventType, selectedStatus]);

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getStatusIcon = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'PROCESSED':
        return <Icon name="HiCheckCircle" className="h-4 w-4 text-green-500" />;
      case 'PROCESSING':
        return <Icon name="HiClock" className="h-4 w-4 text-yellow-500" />;
      case 'FAILED':
        return <Icon name="HiXCircle" className="h-4 w-4 text-red-500" />;
      case 'RECEIVED':
        return <Icon name="HiClock" className="h-4 w-4 text-blue-500" />;
      default:
        return <Icon name="HiClock" className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';
    const normalizedStatus = status.toUpperCase();

    switch (normalizedStatus) {
      case 'PROCESSED':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'PROCESSING':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'FAILED':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'RECEIVED':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Get filter options
  const entityTypes = qboWebhookEventsApi.getEntityTypes();
  const eventTypes = qboWebhookEventsApi.getEventTypes();
  const statuses = qboWebhookEventsApi.getStatuses();

  if (isLoadingEvents && currentPage === 1) {
    return (
      <PageLayout
        title="QBO Webhook Activity"
        description="Monitor and manage QuickBooks Online webhook events and processing status"
        isLoading={true}
        skeletonProps={{
          summaryCardCount: 0,
          tableRows: 0,
          tableColumns: 0,
          showTable: false,
          showSummaryCards: false,
        }}
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-xtnd-blue mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading webhook events...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (eventsError) {
    return (
      <PageLayout
        title="QBO Webhook Activity"
        description="Monitor and manage QuickBooks Online webhook events and processing status"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center">
            <Icon name="HiExclamation" className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Failed to Load QBO Webhook Events
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {eventsError instanceof Error ? eventsError.message : 'An unknown error occurred'}
            </p>
            <Button
              onClick={() => refetchEvents()}
              variant="primary"
              leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
            >
              Retry
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="QBO Webhook Activity"
      description="Monitor and manage QuickBooks Online webhook events and processing status"
      headerContent={
        <Button
          onClick={() => refetchEvents()}
          variant="outline"
          leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
        >
          Refresh
        </Button>
      }
    >
      <div className="space-y-6">

      {/* Statistics Cards */}
      {webhookStats && !isLoadingStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiDocumentText" className="h-8 w-8 text-xtnd-blue" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Events
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.totalEvents.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiCheckCircle" className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Processed</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.byStatus.PROCESSED.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiXCircle" className="h-8 w-8 text-red-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Failed</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.byStatus.FAILED.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiClock" className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Last 24h</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.recentActivity.last24Hours.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card variant="default">
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Label htmlFor="entity-type-filter">Entity Type</Label>
              <Select
                options={[
                  { value: '', label: 'All Entity Types' },
                  ...entityTypes.map(type => ({ value: type, label: type })),
                ]}
                value={selectedEntityType}
                onChange={value =>
                  setSelectedEntityType(Array.isArray(value) ? (value[0] ?? '') : value)
                }
              />
            </div>
            <div className="w-48">
              <Label htmlFor="event-type-filter">Event Type</Label>
              <Select
                options={[
                  { value: '', label: 'All Event Types' },
                  ...eventTypes.map(type => ({ value: type, label: type })),
                ]}
                value={selectedEventType}
                onChange={value =>
                  setSelectedEventType(Array.isArray(value) ? (value[0] ?? '') : value)
                }
              />
            </div>
            <div className="w-48">
              <Label htmlFor="status-filter">Status</Label>
              <Select
                options={[
                  { value: '', label: 'All Statuses' },
                  ...statuses.map(status => ({ value: status.value, label: status.label })),
                ]}
                value={selectedStatus}
                onChange={value =>
                  setSelectedStatus(Array.isArray(value) ? (value[0] ?? '') : value)
                }
              />
            </div>
            {(selectedEntityType || selectedEventType || selectedStatus) && (
              <Button
                onClick={() => {
                  setSelectedEntityType('');
                  setSelectedEventType('');
                  setSelectedStatus('');
                }}
                variant="outline"
                size="sm"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Events Table */}
      <Card variant="default">
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              QBO Webhook Events
            </h2>
            <div className="text-sm text-gray-800 dark:text-gray-300">
              Showing {events.length} events (Page {currentPage})
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {events.length === 0 ? (
            <div className="text-center py-8 text-gray-800 dark:text-gray-300">
              <Icon name="HiDocumentText" className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                No QBO Webhook Events Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {selectedEntityType || selectedEventType || selectedStatus
                  ? 'No events found matching your filters.'
                  : 'No webhook events have been received yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-left text-xs font-medium uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3">
                      Received At
                    </th>
                    <th className="px-6 py-3">
                      Entity Type
                    </th>
                    <th className="px-6 py-3">
                      Event Type
                    </th>
                    <th className="px-6 py-3">
                      Entity ID
                    </th>
                    <th className="px-6 py-3">
                      Status
                    </th>
                    <th className="px-6 py-3">
                      Company ID
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {events.map(event => (
                    <tr key={event.eventId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(event.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                          {event.entityType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full">
                          {event.eventType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {event.entityId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(event.status)}
                          <span className={getStatusColor(event.status) + ' ml-2'}>
                            {event.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">
                        {event.companyId}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {events.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center text-sm text-gray-800 dark:text-gray-200">
                <span>
                  Showing page {currentPage} of {Math.ceil(totalCount / pageSize)} (
                  {totalCount.toLocaleString()} total events)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  leftIcon={<Icon name="HiChevronLeft" className="h-4 w-4" />}
                >
                  Previous
                </Button>
                <Button
                  onClick={handleNextPage}
                  disabled={!hasMore}
                  variant="outline"
                  size="sm"
                  rightIcon={<Icon name="HiChevronRight" className="h-4 w-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
      </div>
    </PageLayout>
  );
}
